import { installmentScheduleTypeConstants } from "constants/account";
import { orderTypeConstants } from "constants/order";
import { distributionLogicLabelList } from "constants/paymentPlanConstants";
import dayjs from "dayjs";
import { dollarsToCents } from "helpers/price";
import {
  addMonthsLikeBackend,
  addWeeksLikeBackend,
  adjustInstallmentDateForWeekend,
} from "pages/OrderOverview/components/Overview/helpers";
import { useMemo, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import styled from "styled-components";
import { IAccountResponse } from "types/Account/AccountResponse";

import {
  buildPlanPreviewDetail,
  buildPlanPreviewSummary,
  IPlanPreviewInput,
} from "../helper";

export const StyledPriceContainer = styled.div`
  border-radius: 12px;
  background: var(--brand-gray-50);
  border: 1px solid var(--brand-gray-200);

  p {
    color: #706f7b;
  }
  .highlighted {
    color: #17143f;
  }

  .preview-icon {
    flex-shrink: 0;
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 10px;

    display: flex;
    align-items: center;
    justify-content: center;

    background: color-mix(
      in srgb,
      var(--brand-purple-700) 8%,
      var(--brand-white)
    );
    color: var(--brand-purple-700);
  }
`;

export function PaymentPlanDetails({
  accountData,
  estimatedCost,
  initialPayment,
}: {
  accountData: IAccountResponse;
  estimatedCost: number;
  initialPayment: number;
}) {
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();
  const minDownPaymentTypeField = watch("paymentPlanMinDownPaymentType");
  const minDownPaymentPercentField = watch("paymentPlanMinDownPaymentPercent");
  const flatAmountField = watch("paymentPlanMinDownPaymentFlat");
  const paymentPlanDurationField = watch("paymentPlanDuration");
  const paymentPlanDurationFullField = watch("paymentPlanDurationFull");
  const dateOfServiceField = watch("dateOfService");
  const isSetAmountField = watch("isSetAmount");
  const installmentAmountField = watch("installmentAmount");
  const installmentFrequencyField = watch("installmentFrequency");
  const providerDistributionLogicField = watch("providerDistributionLogic");
  const orderTypeField = watch("orderType");

  const monthlyPayment = useMemo(() => {
    const remainingAmount = estimatedCost - initialPayment;
    if (isSetAmountField) {
      return dollarsToCents(installmentAmountField || 0);
    }
    return Math.floor((remainingAmount / paymentPlanDurationField) * 100) / 100;
  }, [
    estimatedCost,
    initialPayment,
    paymentPlanDurationField,
    isSetAmountField,
    installmentAmountField,
  ]);

  // Calculate payment plan duration and last payment when isSetAmountField is true
  const calculatedDuration = useMemo(() => {
    if (
      !isSetAmountField ||
      !installmentAmountField ||
      installmentAmountField <= 0
    ) {
      return null;
    }
    const remainingAmount = estimatedCost - initialPayment;
    const installmentAmountInCents = dollarsToCents(installmentAmountField);
    if (installmentAmountInCents <= 0) return null;

    const numberOfPayments = Math.ceil(
      remainingAmount / installmentAmountInCents
    );
    return numberOfPayments;
  }, [isSetAmountField, installmentAmountField, estimatedCost, initialPayment]);

  const calculatedLastPayment = useMemo(() => {
    if (
      !isSetAmountField ||
      !installmentAmountField ||
      !calculatedDuration ||
      calculatedDuration <= 0
    ) {
      return null;
    }
    const remainingAmount = estimatedCost - initialPayment;
    const installmentAmountInCents = dollarsToCents(installmentAmountField);
    const regularPaymentsTotal =
      installmentAmountInCents * (calculatedDuration - 1);
    const lastPayment = remainingAmount - regularPaymentsTotal;
    return lastPayment;
  }, [
    isSetAmountField,
    installmentAmountField,
    calculatedDuration,
    estimatedCost,
    initialPayment,
  ]);

  // Update payment plan duration when isSetAmountField is true
  useEffect(() => {
    if (isSetAmountField && calculatedDuration && calculatedDuration > 0) {
      setValue("paymentPlanDuration", calculatedDuration);
      setValue("paymentPlanDurationFull", calculatedDuration + 1);
    }
  }, [isSetAmountField, calculatedDuration, setValue]);

  const paymentsTimeline = useMemo(() => {
    const remainingAmount = estimatedCost - initialPayment;
    const lastPayment =
      remainingAmount - monthlyPayment * (paymentPlanDurationField - 1);
    const { installmentScheduleType } = accountData.data;
    const isTwoWeekSchedule =
      installmentScheduleType ===
      installmentScheduleTypeConstants.TWO_WEEKS_BEFORE;
    let startDate: dayjs.Dayjs;
    let addAMonth = true;
    const parsedDate = dateOfServiceField
      ? dayjs.utc(dateOfServiceField, "MM/DD/YYYY", true)
      : dayjs.utc();
    const normalizedDateOfService = parsedDate.isValid()
      ? parsedDate.toISOString()
      : dayjs.utc().toISOString();
    if (isTwoWeekSchedule) {
      const twoWeeksBefore = dayjs
        .utc(normalizedDateOfService)
        .subtract(14, "days");
      const now = dayjs.utc();
      startDate = twoWeeksBefore.isBefore(now) ? dayjs() : twoWeeksBefore;
      addAMonth = !!twoWeeksBefore.isBefore(now);
    } else {
      startDate = dayjs();
    }
    // Convert startDate to a native Date once, then do backend-style setMonth math
    const startNativeDate = startDate.toDate();

    const frequency =
      isSetAmountField && installmentFrequencyField
        ? installmentFrequencyField
        : "Monthly";

    const installments = Array.from(
      { length: paymentPlanDurationField },
      (_, index) => {
        let date: string;
        if (frequency === "Weekly") {
          const weeksFromStart = addAMonth ? index + 1 : index;
          const dueNative = adjustInstallmentDateForWeekend(
            addWeeksLikeBackend(startNativeDate, weeksFromStart)
          );
          date = dayjs(dueNative).format("YYYY-MM-DD");
        } else if (frequency === "Biweekly") {
          const twoWeekPeriodsFromStart = addAMonth ? index + 1 : index;
          const dueNative = adjustInstallmentDateForWeekend(
            addWeeksLikeBackend(startNativeDate, twoWeekPeriodsFromStart * 2)
          );
          date = dayjs(dueNative).format("YYYY-MM-DD");
        } else {
          const updatedIndex = addAMonth ? index + 1 : index;
          const dueNative = adjustInstallmentDateForWeekend(
            addMonthsLikeBackend(startNativeDate, updatedIndex)
          );
          date = dayjs(dueNative).format("YYYY-MM-DD");
        }
        const payment =
          index === paymentPlanDurationField - 1 ? lastPayment : monthlyPayment;

        return { date, payment };
      }
    );
    const initialPaymentObj = {
      date: "Due Today",
      payment: initialPayment,
    };
    return [initialPaymentObj, ...installments];
  }, [
    accountData,
    initialPayment,
    monthlyPayment,
    paymentPlanDurationField,
    dateOfServiceField,
    isSetAmountField,
    installmentFrequencyField,
  ]);

  const formIsNotValid = useMemo(() => {
    if (!dateOfServiceField) {
      return true;
    }
    if (
      paymentPlanDurationFullField === null ||
      paymentPlanDurationFullField === undefined
    ) {
      return true;
    }
    if (
      minDownPaymentTypeField === "Percentage" &&
      (minDownPaymentPercentField === null ||
        minDownPaymentPercentField === undefined)
    ) {
      return true;
    }
    if (
      minDownPaymentTypeField === "Flat amount" &&
      (flatAmountField === null || flatAmountField === undefined)
    ) {
      return true;
    }
    return false;
  }, [
    paymentPlanDurationFullField,
    minDownPaymentTypeField,
    minDownPaymentPercentField,
    flatAmountField,
    dateOfServiceField,
  ]);

  // Everything the old row-by-row breakdown showed, folded into the two
  // sentences the design asks for.
  const previewInput: IPlanPreviewInput = {
    downPaymentInCents: initialPayment,
    installmentCount: paymentPlanDurationField,
    installmentAmountInCents: monthlyPayment,
    frequencyLabel:
      isSetAmountField && installmentFrequencyField
        ? installmentFrequencyField.toLowerCase()
        : "monthly",
    totalPaymentCount:
      paymentPlanDurationFullField ?? paymentPlanDurationField + 1,
    // Patient balance estimates pay one provider, so there is nothing to distribute.
    distributionLabel:
      orderTypeField === orderTypeConstants.PATIENT_RESPONSIBILITY
        ? null
        : distributionLogicLabelList[
            providerDistributionLogicField as keyof typeof distributionLogicLabelList
          ] ?? null,
    lastPaymentDate:
      paymentsTimeline[paymentsTimeline.length - 1]?.date ?? null,
    finalPaymentInCents:
      isSetAmountField &&
      calculatedLastPayment !== null &&
      calculatedLastPayment !== monthlyPayment &&
      calculatedLastPayment > 0 &&
      paymentPlanDurationField > 1
        ? calculatedLastPayment
        : null,
    totalCostInCents: estimatedCost,
  };

  return (
    <StyledPriceContainer className="flex align-items-start gap-3 p-4">
      <span className="preview-icon">
        <i className="pi pi-calendar" />
      </span>

      <div className="flex flex-column gap-1 flex-1" style={{ minWidth: 0 }}>
        <p className="text-base font-semibold highlighted line-height-3">
          Plan preview
        </p>

        {formIsNotValid ? (
          <p className="text-sm font-normal line-height-3">
            Fill all required fields to calculate the payment schedule.
          </p>
        ) : (
          <>
            <p className="text-sm font-normal line-height-3">
              {buildPlanPreviewSummary(previewInput)}
            </p>
            <p className="text-sm font-normal line-height-3">
              {buildPlanPreviewDetail(previewInput)}
            </p>
          </>
        )}
      </div>
    </StyledPriceContainer>
  );
}
